import Thumbor from '@common_image';
import Link from 'next/link';
import Popup from '@core_modules/theme/plugins/magebirdpopup/components';
import CmsRenderer from '@core_modules/cms/components/cms-renderer';
import { setCookiesWithExpires, getCookiesSingle } from '@helper_cookies';
import { COLORS } from '@theme_vars';
import useMediaQuery from '@hook/useMediaQuery';
import { useWindowSize } from '@uidotdev/usehooks';
import { BREAKPOINTS } from '@core/theme/vars';
import gqlService from '@core_modules/theme/services/graphql';

const ContentImage = (props) => {
    const { data, storeConfig } = props;

    return (
        <div className="relative">
            {data?.url_image_link ? (
                <Link href={data?.url_image_link}>
                    <Thumbor
                        src={data.image}
                        quality={80}
                        alt={data.title}
                        storeConfig={storeConfig}
                        useContainer={false}
                        magezon={false}
                        className="w-full h-full"
                    />
                </Link>
            ) : (
                <Thumbor
                    src={data.image}
                    quality={80}
                    alt={data.title}
                    storeConfig={storeConfig}
                    useContainer={false}
                    magezon={false}
                    className="w-full h-full"
                />
            )}
        </div>
    );
};

const ContentCustom = (props) => {
    const { data, storeConfig } = props;

    return (
        <>
            <div className="relative content-custom">
                <CmsRenderer content={data.content} storeConfig={storeConfig} />
            </div>
            <style jsx>
                {`
                    :global(.content-custom) {
                        font-family: var(--font-pwa-default_font) !important;
                    }
                `}
            </style>
        </>
    );
};

const CoreContentPopup = (props) => {
    const { data, storeConfig, dataPopup } = props;

    const [open, setOpen] = React.useState(false);
    const [hasScrolled, setHasScrolled] = React.useState(false);
    const { isMobile, isDesktop } = useMediaQuery();
    const size = useWindowSize();
    const isTablet = size.width >= BREAKPOINTS.md && size.width < BREAKPOINTS.lg;
    const popupRef = React.useRef(null);

    const timeout = parseInt(data.seconds, 10) * 1000;
    const targetY = parseInt(data.scroll_px, 10);
    const closeOutside = data.close_when_clicked_outside_popup === '1';
    const autoCloseTimeout = parseInt(data.close_automatically_after_x_seconds, 10) * 1000;
    const overlayBackground = data.overlay_background;
    const popupCornerStyle = data.popup_corner_style;
    const popupBorderRadius = parseInt(data.border_corner_radius_in_px, 10) || 6;
    const popupBorderColor = data.border_corner_color;
    const popupBorderSize = parseInt(data.border_corner_size, 10) || 0;
    const popupBackgroundColor = data.popup_background_color || COLORS.neutral.white;
    const popupPadding = parseInt(data.padding_size, 10) || 12;
    const popupShadow = data.popup_box_shadow;
    const popupAppearEffect = data.appear_effect;
    const popupVerticalPosition = data.vertical_position;
    const popupVerticalPX = parseInt(data.vertical_px, 10) || 0;
    const popupHorizontalPosition = data.horizontal_position;
    const popupHorizontalPX = parseInt(data.horizontal_px_defined_position, 10) || 0;
    const customCss = data?.custom_css;
    const popupDeviceDisplay = data?.devices;
    const popupContentWidth = data.popup_content_width;
    const popupId = data?.id;
    const stopFurtherPopups = dataPopup?.filter((item) => item.stop_further === '1') || [];

    const [setMagebirdPopupAction] = gqlService.setMagebirdPopupAction();

    const popupWidth = {
        width: popupContentWidth !== '0px' && popupContentWidth !== '0%' ? popupContentWidth : 'auto',
    };

    // Popup corner style
    let cornerStyle = {};
    // corner style
    switch (popupCornerStyle) {
    case '1':
        cornerStyle = {
            borderRadius: popupBorderRadius,
        };
        break;
    case '2':
        cornerStyle = {
            borderRadius: '50%',
        };
        break;
    default:
        cornerStyle = {
            borderRadius: 0,
        };
        break;
    }

    // Border color
    if (popupBorderColor) {
        cornerStyle = {
            ...cornerStyle,
            borderColor: popupBorderColor,
        };
    }

    // Border Size
    if (popupBorderSize) {
        cornerStyle = {
            ...cornerStyle,
            borderWidth: popupBorderSize,
        };
    }

    // Background Color
    if (popupBackgroundColor) {
        cornerStyle = {
            ...cornerStyle,
            backgroundColor: popupBackgroundColor,
        };
    }

    // Popup padding
    if (popupPadding) {
        cornerStyle = {
            ...cornerStyle,
            padding: popupPadding,
        };
    }

    // Popup appear effect
    let animation = '';
    switch (popupAppearEffect) {
    case '2':
        animation = 'animate-anima-fade-in';
        break;
    case '3':
        animation = 'animate-anima-slide-down';
        break;
    case '4':
        animation = 'animate-anima-slide-up';
        break;
    case '6':
        animation = 'animate-anima-elastic';
        break;
    case '7':
        animation = 'animate-anima-zoom-rotate';
        break;
    default:
        animation = 'animate-anima-fade-in-fast';
        break;
    }

    // with overlay background handle;
    let withbackdrop = true;
    switch (overlayBackground) {
    case '1':
        withbackdrop = true;
        break;
    case '2':
        withbackdrop = true;
        break;
    case '3':
        withbackdrop = false;
        break;
    case '4':
        withbackdrop = false;
        break;
    default:
        withbackdrop = true;
        break;
    }

    // Popup Horizontal Position
    let horizontalPositionStyle = {};
    switch (popupHorizontalPosition) {
    case '1':
        horizontalPositionStyle = {
            width: 'fit-content',
            left: '50%',
            transform: 'translatex(-50%)',
        };
        break;
    case '2':
        horizontalPositionStyle = {
            width: 'fit-content',
            left: popupHorizontalPX,
        };
        break;
    case '3':
        horizontalPositionStyle = {
            width: 'fit-content',
            right: popupHorizontalPX + 16,
        };
        break;
    case '4':
        horizontalPositionStyle = {
            paddingRight: popupHorizontalPX,
        };
        break;
    case '5':
        horizontalPositionStyle = {
            paddingLeft: popupHorizontalPX,
        };
        break;
    case '6':
        horizontalPositionStyle = {
            width: 'fit-content',
            left: 0,
        };
        break;
    default:
        horizontalPositionStyle = {};
        break;
    }

    // Popup Vertical Position
    let verticalPositionStyle = {};
    switch (popupVerticalPosition) {
    case '1':
        verticalPositionStyle = {
            position: 'fixed',
            bottom: 'auto',
            top: popupVerticalPX + 14,
            left: 'auto',
            transform: 'none',
        };
        break;
    case '2':
        verticalPositionStyle = {
            position: 'fixed',
            bottom: popupVerticalPX,
            top: 'auto',
            left: 'auto',
            transform: 'none',
        };
        break;
    case '3':
        verticalPositionStyle = {
            position: 'fixed',
            bottom: popupVerticalPX,
            top: 'auto',
            left: 'auto',
            transform: 'none',
        };
        break;
    case '4':
        verticalPositionStyle = {
            position: 'relative',
            bottom: 'auto',
            top: 14,
            left: 'auto',
            transform: 'none',
            marginBottom: 16,
        };
        break;
    default:
        verticalPositionStyle = {};
        break;
    }

    // Popup to show based on device
    let deviceType = true;
    switch (popupDeviceDisplay) {
    case '2':
        deviceType = isDesktop;
        break;
    case '3':
        deviceType = isMobile;
        break;
    case '4':
        deviceType = isTablet;
        break;
    case '5':
        deviceType = isMobile || isTablet;
        break;
    case '6':
        deviceType = isDesktop || isTablet;
        break;
    case '7':
        deviceType = isDesktop || isMobile;
        break;
    default:
        deviceType = true;
        break;
    }

    // get start date to handle elapsed time of pop up
    const [startDate, setStartDate] = React.useState(new Date());
    React.useEffect(() => {
        if (open) {
            setStartDate(new Date());
        }
    }, [open]);

    // calculate elapsed time
    const milisecondsDifference = () => {
        const endDate = new Date();
        const milisecondsDiff = endDate.getTime() - startDate.getTime();
        return milisecondsDiff;
    };

    // Send matrix method
    const sendMatrix = (actionType) => {
        const timeElapsed = milisecondsDifference();
        const variables = {
            popup_id: parseInt(popupId, 10),
            action: actionType,
            time: timeElapsed,
        };

        setMagebirdPopupAction({
            variables,
        }).then(() => {
            // window.backdropLoader(false);
            // window.toastMessage({
            //     open: true,
            //     text: 'Success',
            //     variant: 'success',
            // });
        }).catch(() => {
            // window.backdropLoader(false);
            // window.toastMessage({
            //     open: true,
            //     text: e.message,
            //     variant: 'error',
            // });
        });
    };

    // handle send matrix when click inside pop up
    React.useEffect(() => {
        const handleClick = (event) => {
            if (popupRef.current && popupRef.current.contains(event.target)) {
                const type = 'click_inside';
                sendMatrix(type);
            }
        };
        document.addEventListener('click', handleClick);

        return () => {
            document.removeEventListener('click', handleClick);
        };
    }, [startDate]);

    // handle send matrix when click outside popup (pop up closed without intercation)
    const handleSendmatrixOutsideClick = () => {
        if (open) {
            const type = 'popup_closed';
            sendMatrix(type);
        }
    };

    // check page reloaded
    const [isPageReloaded, setIsPageReloaded] = React.useState(false);
    React.useEffect(() => {
        if (window.performance && window.performance.navigation.type === 1) {
            setIsPageReloaded(true);
        }
    }, []);

    // handle send matrix browser window closed of page reloaded
    React.useEffect(() => {
        const handleBeforeUnload = () => {
            if (open) {
                let type = '';
                // page reloaded
                if (isPageReloaded) {
                    type = 'page_reloaded';
                    sendMatrix(type);
                } else if (overlayBackground !== '3' || overlayBackground !== '4') { // window closed
                    type = 'window_closed';
                    sendMatrix(type);
                }
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [open, isPageReloaded]);

    // eslint-disable-next-line no-unneeded-ternary
    const isPopupOpen = getCookiesSingle(`pu_${data.cookie_id}`) !== 'false' ? true : false;

    // track mouse scroll
    const handleScroll = () => {
        const scrollY = window.scrollY || window.pageYOffset; // Handle browser compatibility
        setHasScrolled(scrollY > targetY); // Update state based on scroll position
    };
    // track mouse scroll
    React.useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true }); // Improve performance with passive option
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // toggle open / close popup
    const togglePopup = () => {
        if (stopFurtherPopups.length > 0) {
            const localPopupOpen = !(typeof getCookiesSingle(`pu_${data.cookie_id}`) !== 'undefined'
                && getCookiesSingle(`pu_${data.cookie_id}`) === 'false');
            if (isPopupOpen && localPopupOpen && stopFurtherPopups[0].cookie_id === data.cookie_id) {
                setOpen(true);
            } else {
                setOpen(false);
            }
        } else {
            const localPopupOpen = !(typeof getCookiesSingle(`pu_${data.cookie_id}`) !== 'undefined'
                && getCookiesSingle(`pu_${data.cookie_id}`) === 'false');
            if (isPopupOpen && localPopupOpen) {
                setOpen(true);
            } else {
                setOpen(false);
            }
        }
    };

    // page reload state
    let isReloaded = true;

    // handle close popup by display frequency
    const handleClosePopup = (popup) => {
        switch (popup.display_frequency) {
        case '1':
            setCookiesWithExpires(`pu_${popup.cookie_id}`, 'false', parseFloat(popup.cookie_life_time, 10));
            setOpen(false);
            break;
        case '2':
            setCookiesWithExpires(`pu_${popup.cookie_id}`, 'false', parseFloat(popup.cookie_life_time, 10));
            setOpen(false);
            break;
        case '3':
            setOpen(false);
            break;
        default:
            setCookiesWithExpires(`pu_${popup.cookie_id}`, 'false', parseFloat(popup.cookie_life_time, 10));
            setOpen(false);
            break;
        }
    };

    // handle open popup by specific condition
    React.useEffect(() => {
        if (data) {
            switch (data.show_when_code) {
            case '1':
                togglePopup();
                break;
            case '2':
                setTimeout(() => {
                    togglePopup();
                }, timeout);
                break;
            case '3':
                if (hasScrolled) {
                    togglePopup();
                }
                break;
            case '6':
                if (window !== undefined) {
                    document.addEventListener('mouseleave', () => {
                        if (isReloaded) {
                            togglePopup();
                            isReloaded = false;
                        }
                    });
                    window.addEventListener('blur', () => {
                        if (isReloaded) {
                            togglePopup();
                            isReloaded = false;
                        }
                    });
                }
                break;
            case '7':
                setTimeout(() => {
                    togglePopup();
                }, timeout);
                break;
            default:
                togglePopup();
                break;
            }
        }
    }, [data, hasScrolled]);

    React.useEffect(() => {
        if (autoCloseTimeout > 0) {
            setTimeout(() => {
                setCookiesWithExpires(`pu_${data.cookie_id}`, 'false', parseFloat(data.cookie_life_time, 10));
                setOpen(false);
            }, autoCloseTimeout);
        }
    }, [autoCloseTimeout]);

    const contentProps = {
        data,
        storeConfig,
        open,
        setOpen,
        ContentImage,
        ContentCustom,
        handleClosePopup,
        closeOutside,
        withbackdrop,
        overlayBackground,
        cornerStyle,
        popupShadow,
        animation,
        verticalPositionStyle,
        horizontalPositionStyle,
        customCss,
        deviceType,
        popupWidth,
        popupRef,
        handleSendmatrixOutsideClick,
    };

    return (
        <Popup {...contentProps} />
    );
};

export default CoreContentPopup;
